import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import { CheckIcon, SelectorIcon } from "@heroicons/react/solid";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { Button } from "../../Components/Button";

import { cancelAPIRequest, classNames, getErrorMessages } from "../../Utils";
import { ErrorList } from "../../Components/ErrorList";
import { EmptyList } from "../../Components/EmptyList";
import { useLocation, useNavigate } from "react-router-dom";
import {
  clearBusinessProduct,
  clearBusinessProductError,
  requestAddMainBusiness,
  requestDeleteBusinessProduct,
  requestListBusiness,
  requestListBusinessProduct,
  useAuthDispatch,
  useAuthState,
  useBusinessProductDispatch,
  useBusinessProductState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
} from "../../Context";
import { Pagination } from "../../Components/Pagination";
import TableSkeleton from "../../Components/TableSkeleton";
import { PencilAltIcon, TrashIcon } from "@heroicons/react/outline";
import { ModalConfirm } from "../../Components/ModalConfirm";
import { ModalAlert } from "../../Components/ModalAlert";
import { isBusinessMainBlocked } from "../../Constants/business";

const PRODUCT_TYPE = {
  GOODS: "Goods",
  SERVICES: "Services",
};

export const ProductList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthState();

  const [selectedBusiness, setselectedBusiness] = useState({});
  const [page, setpage] = useState(1);
  const [prodactId, setprodactId] = useState("");

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const businessTransactionDispatch = useBusinessTransactionDispatch();
  const businessProductDispatch = useBusinessProductDispatch();
  const authDispatch = useAuthDispatch();

  const { businesses } = useBusinessTransactionState();

  const {
    request: businessProductRequest,
    businessProducts,
    businessProductsLoading,
    businessProductNextTokens,
    businessProductsError,
    deletedID,
    deleteProductLoading,
    deleteProductError,
  } = useBusinessProductState();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const businessIdParam = searchParams.get("id");

  useEffect(() => {
    clearBusinessProduct(businessProductDispatch);
    clearBusinessProductError(businessProductDispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user.id) {
      requestListBusiness(businessTransactionDispatch, {
        owner: user.id,
        page: 1,
        limit: 10,
      });
    }
  }, [businessTransactionDispatch, user.id]);

  useEffect(() => {
    // Function to set selected business
    const setSelectedBusinessLogic = async () => {
      if (businessIdParam) {
        if (businesses.length > 0) {
          const business = businesses.find(
            (item) => item.value === businessIdParam
          );
          if (business) {
            setselectedBusiness({
              ...business,
              label: business.name,
              value: business.id,
            });
          } else {
            // Handle if business is not found, select index 0
            setselectedBusiness({
              ...businesses[0],
              label: businesses[0].name,
              value: businesses[0].id,
            });
          }
        } else {
          // Handle if businesses are empty, no business to select
        }
      } else if (user.mainBusiness) {
        // Set selectedBusiness directly from user.mainBusiness
        setselectedBusiness({
          ...user.mainBusiness,
          label: user.mainBusiness.name,
          value: user.mainBusiness.id,
        });
      } else if (businesses.length > 0) {
        // Set selected business to the first item in the list
        // Prefer a healthy business: a RESTRICTED / PENDING_DELETION one must
        // not become the account-wide main business. Falls back to the first
        // entry when every business is blocked.
        const mainCandidate =
          businesses.find((business) => !isBusinessMainBlocked(business)) ||
          businesses[0];
        setselectedBusiness({
          ...mainCandidate,
          label: mainCandidate.name,
          value: mainCandidate.id,
        });
        requestAddMainBusiness(authDispatch, {
          businessID: mainCandidate.id,
        });
      } else {
        // Handle if businesses are empty, no business to select
      }
    };

    setSelectedBusinessLogic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businesses, businessIdParam, user.mainBusiness]);

  useEffect(() => {
    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessProductDispatch, selectedBusiness.value]);

  useEffect(() => {
    if (deleteProductError) {
      setShowErrorModal(true);
      setShowConfirmModal(false);
    }
  }, [deleteProductError]);

  useEffect(() => {
    if (!deletedID) {
      return;
    }
    setShowConfirmModal(false);
    setShowSuccessModal(true);
  }, [deletedID]);

  const onDelete = () => {
    requestDeleteBusinessProduct(businessProductDispatch, {
      id: prodactId,
    });
  };

  const onCreate = () => {
    clearBusinessProduct(businessProductDispatch);
    navigate(`/product/create?id=${selectedBusiness.value}`);
  };

  const onLoad = () => {
    if (businessProductRequest) {
      cancelAPIRequest(businessProductRequest);
    }
    if (selectedBusiness.value) {
      requestListBusinessProduct(businessProductDispatch, {
        page: 1,
        limit: 10,
        businessID: selectedBusiness.value,
      });
    }
  };

  const onNext = () => {
    const nextPage = page + 1;
    setpage(nextPage);
    if (businessProductNextTokens[nextPage]) {
      requestListBusinessProduct(businessProductDispatch, {
        page: nextPage,
        limit: 10,
        businessID: selectedBusiness.value,
        nextTokens: businessProductNextTokens[nextPage],
      });
    }
  };

  const onPrev = () => {
    const prevPage = page - 1;
    setpage(prevPage);
    if (prevPage > 0) {
      requestListBusinessProduct(businessProductDispatch, {
        page: prevPage,
        limit: 10,
        businessID: selectedBusiness.value,
        nextTokens: businessProductNextTokens[prevPage],
      });
    }
  };

  const onPageChange = (selectedPage) => {
    setpage(selectedPage);
    if (businessProductNextTokens[selectedPage] || selectedPage === 1) {
      requestListBusinessProduct(businessProductDispatch, {
        page: selectedPage,
        limit: 10,
        businessID: selectedBusiness.value,
        nextTokens: businessProductNextTokens[selectedPage],
      });
    }
  };

  return (
    <div className="space-y-4">
      {deleteProductError && (
        <ModalConfirm
          open={showErrorModal}
          type="ALERT"
          setOpen={(open) => {
            setShowErrorModal(open);
          }}
          title="Error"
          description={getErrorMessages(deleteProductError)}
          onConfirm={() => clearBusinessProductError(businessProductDispatch)}
        />
      )}
      <ModalConfirm
        open={showSuccessModal}
        type="SUCCESS"
        setOpen={(open) => {
          setShowSuccessModal(open);
        }}
        title="Success"
        description="The product has been successfully deleted"
        onConfirm={() => clearBusinessProduct(businessProductDispatch)}
      />
      <ModalAlert
        open={showConfirmModal}
        setOpen={(open) => {
          setShowConfirmModal(open);
        }}
        title="Delete Product"
        description="Products can only be deleted if they have not been used in any transaction. Continue?"
        confirmButtonText="Yes"
        cancelButtonText="Cancel"
        loadingButton={deleteProductLoading}
        disabledButton={deleteProductLoading}
        onConfirm={onDelete}
      />
      <div className="grid gird-cols-1 sm:grid-cols-2 gap-y-4 mb-4">
        <div className="max-w-md md:max-w-2xl">
          <Listbox value={selectedBusiness} onChange={setselectedBusiness}>
            {({ open }) => (
              <>
                <ListboxButton className="flex items-center justify-between w-full sm:w-48 py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm sm:text-sm">
                  <span className="max-w-full ml-3 mr-2 text-sm text-ellipsis overflow-hidden sm:whitespace-nowrap">
                    {selectedBusiness?.label || t("Business Name")}
                  </span>
                  <SelectorIcon className="w-5 h-5 text-gray-500" />
                </ListboxButton>
                <Transition
                  show={open}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-11/12 sm:w-auto rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    {businesses.map((business) => (
                      <ListboxOption
                        key={business.value}
                        className={({ focus }) =>
                          classNames(
                            focus ? "text-white bg-blue-600" : "text-gray-900",
                            "cursor-default select-none relative py-2 pl-3 pr-9"
                          )
                        }
                        value={business}
                      >
                        {({ selected, focus }) => (
                          <>
                            <div className="flex items-center justify-between">
                              <span
                                className={classNames(
                                  selected ? "font-semibold" : "font-normal",
                                  "ml-3 mr-2 block truncate"
                                )}
                              >
                                {business.label}
                              </span>
                              {selected ? (
                                <span
                                  className={classNames(
                                    focus ? "text-white" : "text-blue-600",
                                    "absolute inset-y-0 right-0 flex items-center pr-4"
                                  )}
                                >
                                  <CheckIcon className="h-5 w-5" />
                                </span>
                              ) : null}
                            </div>
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </Transition>
              </>
            )}
          </Listbox>
        </div>
        <div className="gap-4 flex justify-start sm:justify-end w-full">
          <Button
            className="w-full sm:w-auto"
            type="button"
            onClick={onCreate}
            title={t("Create New Product")}
          />
        </div>
      </div>
      <div className="flex flex-col">
        <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Name")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Type")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Price")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Reference Number")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Action")}
                    </th>
                  </tr>
                </thead>
                {businessProductsLoading ? (
                  <TableSkeleton rows={10} columns={5} />
                ) : (
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {businessProducts.length > 0 ? (
                      businessProducts.map((product) => {
                        const titleCaseType =
                          PRODUCT_TYPE[product.type] || product.type;

                        return (
                          <tr key={product.id}>
                            <td className="px-4 py-2 font-medium text-sm text-gray-00 whitespace-nowrap ">
                              {product.name}
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                              {t(titleCaseType)}
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                              <NumericFormat
                                displayType={"text"}
                                value={product.price}
                                thousandSeparator={"."}
                                decimalSeparator={","}
                                prefix={"Rp "}
                              />
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                              {t(product.reference)}
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                              <div className="flex flex-auto gap-2">
                                <button
                                  onClick={() =>
                                    navigate(`/product/edit/${product.id}`)
                                  }
                                >
                                  <PencilAltIcon className="h-5 w-5 text-blue-500" />
                                </button>
                                <button
                                  onClick={() => {
                                    setShowConfirmModal(true);
                                    setprodactId(product.id);
                                  }}
                                >
                                  <TrashIcon className="h-5 w-5 text-red-500" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td className="p-4" colSpan={8}>
                          {businessProductsError ? (
                            <ErrorList
                              message={
                                businessProductsError?.message ||
                                "Error fetching products."
                              }
                              errors={businessProductsError?.errors}
                            />
                          ) : (
                            <EmptyList title="No Product" />
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                )}
              </table>
              <Pagination
                page={page}
                onPrev={onPrev}
                onNext={onNext}
                onPageChange={onPageChange}
                nextTokens={businessProductNextTokens}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
